import React from 'react';
import { css } from '@emotion/react';
import { Controller } from 'react-hook-form';
import TextareaAutosize, { TextareaAutosizeProps } from 'react-textarea-autosize';

import t from 'react-translate';
import { mergeRefs } from 'shared/react-utils';
import NvPopover from 'shared/components/nv-popover';
import { danger, gray1, gray2, gray3 } from 'styles/global_defaults/colors';
import { almostBlack, novoEdBlue } from 'athena/styles/colors';
import ValidationErrorMessage from 'shared/components/inputs/validation-error-message';
import { openSans, textMediumFontSize, textMediumLineHeight } from 'styles/global_defaults/fonts';
import { doubleSpacing, halfSpacing, largeSpacing, quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';

type BaseAthenaTextareaProps = Omit<TextareaAutosizeProps, 'ref' | 'className'> & {
  error?: string;
  className?: string;
  showLabel?: boolean;
  inputClassName?: string;
  iconPosition?: 'left' | 'right';
  dataQa?: string;
  dataQaId?: string;
  validationErrorTitle?: string;
};

const BaseAthenaTextInput = React.forwardRef<HTMLTextAreaElement, BaseAthenaTextareaProps>((props, ref) => {
  const {
    error,
    onBlur,
    onFocus,
    required,
    onChange,
    className,
    placeholder,
    inputClassName,
    label,
    showLabel = true,
    value: propsValue,
    iconPosition = 'left',
    dataQa,
    dataQaId,
    validationErrorTitle,
    ...restProps
  } = props;

  const [value, setValue] = React.useState(propsValue);
  const [isFocused, setIsFocused] = React.useState(false);

  React.useEffect(() => {
    setValue(propsValue);
  }, [propsValue]);

  const styles = css`
    .input-label {
      left: 0;
      font-weight: 600;
      color: ${gray1};
      top: -${standardSpacing}px;
    }

    .athena-input-container {
      .athena-input {
        width: 100%;
        min-height: 100px;
        padding: ${halfSpacing}px;
        border-radius: ${quarterSpacing}px;
        color:  ${value ? almostBlack : gray2};
        border: 1px solid ${error ? danger : gray3};

        // Defining font styles explicitely and not using 'text-medium' class in
        // input because of a specificity issue.
        font-family: ${openSans};
        font-size: ${textMediumFontSize}px!important;
        line-height: ${textMediumLineHeight}px!important;

        ${required && css`
          padding-right: ${largeSpacing}px;
        `};

        &:focus {
          outline: 1px solid ${novoEdBlue};
        }

        &::placeholder {
          color: ${gray2};
        }
      }

      .required-indicator {
        top: 0;
        right: 0;
        width: ${largeSpacing}px;
        height: ${doubleSpacing}px;
      }
    }
  `;

  const handleChange = (e) => {
    setValue(e.target.value);

    onChange?.(e);
  };

  const handleFocus = (e) => {
    setIsFocused(true);

    onFocus?.(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);

    onBlur?.(e);
  };

  return (
    <div css={styles} className={className} data-qa={dataQa} data-qa-id={dataQaId}>
      {showLabel && (
        <div className='input-label text-small mb-1'>
          {label || placeholder}
        </div>
      )}
      <div className='athena-input-container position-relative'>
        <NvPopover
          className='nv-text-input'
          show={!!error && isFocused}
          // NOTE: Using existing translation to not create a new one.
          content={<ValidationErrorMessage text={error} title={validationErrorTitle || t.VALIDATION.ERROR()} />}
        >
          <TextareaAutosize
            type='text'
            value={value}
            ref={ref as any}
            onBlur={handleBlur}
            required={required}
            onFocus={handleFocus}
            onChange={handleChange}
            placeholder={placeholder}
            className={`athena-input ${inputClassName ?? ''}`}
            aria-label={label || placeholder}
            {...restProps}
          />
        </NvPopover>
        {required && (
          <div className='required-indicator position-absolute d-flex align-items-center justify-content-center text-medium gray-2'>
            *
          </div>
        )}
      </div>
    </div>
  );
});

type Props = BaseAthenaTextareaProps & {
  withForm?: boolean;
};

const AthenaTextarea = React.forwardRef<HTMLTextAreaElement, Props>((props, ref) => {
  const {
    name,
    value,
    error,
    onBlur,
    onChange,
    withForm,
    ...restProps
  } = props;

  return withForm ? (
    <Controller
      name={name}
      render={({ field, fieldState }) => {
        const handleBlur = (e) => {
          onBlur?.(e);
          field.onBlur();
        };

        const handleChange = (e) => {
          onChange?.(e);
          field.onChange(e);
        };

        return (
          <BaseAthenaTextInput
            name={field.name}
            onBlur={handleBlur}
            onChange={handleChange}
            value={value ?? field.value}
            error={error ?? fieldState.error?.message}
            {...restProps}
            ref={mergeRefs(ref, field.ref)}
          />
        );
      }}
    />
  ) : (
    <BaseAthenaTextInput
      name={name}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
      {...restProps}
      ref={ref}
    />
  );
});

export default AthenaTextarea;
